<script setup>
import { vElementVisibility } from "@vueuse/components";

const props = defineProps({ setCursor: Function });
const handle_len_rate = ref(2.4);
const circlePaths = ref([]);
const connections = ref(null);
const circle1 = ref(null);
const circle2 = ref(null);
const circle3 = ref(null);
const largeCircle = ref(null);
const project = ref(null);
const ballRadius = ref(1);
const showAnimation = ref(false);

const hwh = ref(null);

const visibilityChanged = (isVisible) => {
  if (!showAnimation.value) {
    showAnimation.value = isVisible;
  }
};

onMounted(() => {
  paper.setup(hwh.value);
  project.value = paper.project;

  circle1.value = new paper.Path.Circle({
    center: [385, 330],
    radius: ballRadius.value,
    fillColor: "#FFC800",
  });
  circlePaths.value.push(circle1.value);

  circle2.value = new paper.Path.Circle({
    center: [828, 330],
    radius: ballRadius.value,
    fillColor: "#FFC800",
  });
  circlePaths.value.push(circle2.value);

  circle3.value = new paper.Path.Circle({
    center: [1271, 330],
    radius: ballRadius.value,
    fillColor: "#FFC800",
  });
  circlePaths.value.push(circle3.value);

  largeCircle.value = new paper.Path.Circle({
    center: [-100, -100],
    radius: 42,
    fillColor: "#FFC800",
  });
  circlePaths.value.push(largeCircle.value);

  connections.value = new paper.Group();
  generateConnections(circlePaths.value);

  project.value.view.onFrame = (event) => {
    if (showAnimation.value) {
      if (circle1.value.bounds.width < 120) {
        circle1.value.scale(1.07);
      }

      if (circle1.value.bounds.width > 10) {
        if (circle2.value.bounds.width < 120) {
          circle2.value.scale(1.07);
        }
      }

      if (circle2.value.bounds.width > 10) {
        if (circle3.value.bounds.width < 120) {
          circle3.value.scale(1.07);
        }
      }
    }
  };

  project.value.view.onMouseMove = (event) => {
    const bounds = {
      minX: 42,
      minY: 42,
      maxX: hwh.value.clientWidth - 42,
      maxY: hwh.value.clientHeight - 42,
    };

    if (event.point.x < bounds.minX) event.point.x = bounds.minX;
    if (event.point.y < bounds.minY) event.point.y = bounds.minY;
    if (event.point.x > bounds.maxX) event.point.x = bounds.maxX;
    if (event.point.y > bounds.maxY) event.point.y = bounds.maxY;

    largeCircle.value.position = event.point;
    generateConnections(circlePaths.value);
  };
});

const onMouseOut = () => {
  largeCircle.value.position = [-100, -100];
};

const generateConnections = (paths) => {
  connections.value.children = [];
  for (let i = 0, l = paths.length; i < l; i++) {
    for (let j = i - 1; j >= 0; j--) {
      const maxD = 250;
      const path = metaball(
        paths[i],
        paths[j],
        0.5,
        handle_len_rate.value,
        maxD,
      );

      if (path) {
        connections.value.appendTop(path);
        path.removeOnMove();
      }
    }
  }
};

const metaball = (ball1, ball2, v, handle_len_rate, maxDistance) => {
  const center1 = ball1.position;
  const center2 = ball2.position;
  let radius1 = ball1.bounds.width / 2;
  let radius2 = ball2.bounds.width / 2;
  const pi2 = Math.PI / 2;
  const d = center1.getDistance(center2);
  let u1, u2;

  if (radius1 == 0 || radius2 == 0) return;

  if (d > maxDistance || d <= Math.abs(radius1 - radius2)) {
    return;
  } else if (d < radius1 + radius2) {
    // case circles are overlapping
    u1 = Math.acos(
      (radius1 * radius1 + d * d - radius2 * radius2) / (2 * radius1 * d),
    );
    u2 = Math.acos(
      (radius2 * radius2 + d * d - radius1 * radius1) / (2 * radius2 * d),
    );
  } else {
    u1 = 0;
    u2 = 0;
  }

  const angle1 = center2.subtract(center1).getAngleInRadians();
  const angle2 = Math.acos((radius1 - radius2) / d);
  const angle1a = angle1 + u1 + (angle2 - u1) * v;
  const angle1b = angle1 - u1 - (angle2 - u1) * v;
  const angle2a = angle1 + Math.PI - u2 - (Math.PI - u2 - angle2) * v;
  const angle2b = angle1 - Math.PI + u2 + (Math.PI - u2 - angle2) * v;
  const p1a = center1.add(getVector(angle1a, radius1));
  const p1b = center1.add(getVector(angle1b, radius1));
  const p2a = center2.add(getVector(angle2a, radius2));
  const p2b = center2.add(getVector(angle2b, radius2));

  // define handle length by the distance between
  // both ends of the curve to draw
  const totalRadius = radius1 + radius2;
  let d2 = Math.min(
    v * handle_len_rate,
    p1a.subtract(p2a).length / totalRadius,
  );

  // case circles are overlapping:
  d2 *= Math.min(1, (d * 2) / (radius1 + radius2));

  radius1 *= d2;
  radius2 *= d2;

  const path = new paper.Path({
    segments: [p1a, p2a, p2b, p1b],
    style: ball2.style,
    closed: true,
  });
  const segments = path.segments;
  segments[0].handleOut = getVector(angle1a - pi2, radius1);
  segments[1].handleIn = getVector(angle2a + pi2, radius2);
  segments[2].handleOut = getVector(angle2b - pi2, radius2);
  segments[3].handleIn = getVector(angle1b + pi2, radius1);
  return path;
};

const getVector = (radians, length) => {
  return new paper.Point({
    angle: (radians * 180) / Math.PI,
    length,
  });
};
</script>

<template>
  <div class="relative" @mouseout="onMouseOut">
    <canvas ref="hwh" class="hidden lg:block" />

    <div class="lg:flex justify-center">
      <div class="max-w-7xl w-full">
        <div class="md:flex">
          <h2
            class="h1 px-6 py-3 mb-6 md:mb-16 relative z-10 pointer-events-none"
          >
            {{ $t("about.howWeHelp.title.part1") }} <br />
            {{ $t("about.howWeHelp.title.part2") }}
          </h2>
        </div>
      </div>
    </div>

    <div v-element-visibility="visibilityChanged" class="flex justify-center">
      <div class="max-w-7xl w-full">
        <div
          class="grid grid-cols-12 md:gap-10 circles"
          :class="{ visible: showAnimation }"
        >
          <div class="col-span-12 md:col-span-4">
            <div class="px-6 relative z-10 pointer-events-none">
              <h2
                class="mb-2.5 md:mb-4"
                v-html="$t('about.howWeHelp.weSupport.title')"
              />
              <p
                class="mb-4 md:mb-0"
                v-html="$t('about.howWeHelp.weSupport.subtitle')"
              />
            </div>
          </div>
          <div class="col-span-12 md:col-span-4">
            <div class="px-6 relative z-10 pointer-events-none">
              <h2
                class="mb-2.5 md:mb-4"
                v-html="$t('about.howWeHelp.weEducate.title')"
              />
              <p
                class="mb-4 md:mb-0"
                v-html="$t('about.howWeHelp.weEducate.subtitle')"
              />
            </div>
          </div>
          <div class="col-span-12 md:col-span-4">
            <div class="px-6 relative z-10 pointer-events-none">
              <h2
                class="mb-2.5 md:mb-4"
                v-html="$t('about.howWeHelp.weConnect.title')"
              />
              <p v-html="$t('about.howWeHelp.weConnect.subtitle')" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
canvas {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -45%);
  width: 2000px;
  margin: 0 auto;
  height: 160%;
}

.circles:deep(h2) {
  position: relative;

  &::before {
    @media screen and (max-width: 1280px) {
      position: absolute;
      z-index: -1;
      content: "";
      top: -22px;
      left: -32px;
      width: 0;
      height: 0;
      transition: 0.25s;
      background-color: #ffc800;
      border-radius: 100%;
    }
  }
}

.circles.visible:deep(h2) {
  &::before {
    @media screen and (max-width: 1280px) {
      width: 80px;
      height: 80px;
    }
  }
}
</style>
